
export const detectCORSError = (error) => {
  if (error.message && error.message.includes('CORS policy')) {
    return {
      isCORS: true,
      message: 'CORS policy blocked the request',
      solution: 'Switch to localhost API or configure Firebase Functions CORS'
    };
  }
  return { isCORS: false };
};

// API URL Fallback Strategy
export const getFallbackApiUrl = () => {
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  
  if (isLocalhost) {
    return 'http://localhost:5000';
  }
  
  // Force production API for web.app domains
  if (window.location.hostname.includes('web.app')) {
    return 'https://wyenfos-api-615596991593.asia-south1.run.app';
  }
  
  return 'https://wyenfos-api-615596991593.asia-south1.run.app';
};

// CORS-friendly request wrapper
export const corsSafeRequest = async (url, options = {}) => {
  // If the URL is a Firebase Functions URL and we're on localhost, try localhost first
  const firebaseUrls = [
    'api-aovv3tvopa-uc.a.run.app',
    'us-central1-wyenfosbills.cloudfunctions.net',
    'api-wyenfosbills-us-central1.cloudfunctions.net'
  ];
  
  const isFirebaseUrl = firebaseUrls.some(firebaseUrl => url.includes(firebaseUrl));
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isFirebaseUrl && isLocalhost) {
    let localhostUrl = url;
    firebaseUrls.forEach(firebaseUrl => {
      localhostUrl = localhostUrl.replace(`https://${firebaseUrl}`, 'http://localhost:5000');
    });
    
    try {
      console.log('🔄 Trying localhost API first:', localhostUrl);
      const response = await fetch(localhostUrl, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
      
      if (response.ok) {
        console.log('✅ Localhost API successful');
        return response;
      }
    } catch (error) {
      console.log('❌ Localhost API failed, trying Firebase Functions:', error.message);
    }
  }
  
  // Fallback to original URL
  try {
    console.log('🔄 Trying Firebase Functions API:', url);
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    return response;
  } catch (error) {
    const corsError = detectCORSError(error);
    if (corsError.isCORS) {
      console.error('🚫 CORS Error detected:', corsError.message);
      console.log('💡 Solution: Use localhost API or configure Firebase Functions CORS');
    }
    throw error;
  }
};

// Smart API Client with CORS handling
export class CORSSmartClient {
  constructor() {
    this.baseUrl = this.detectBestBaseUrl();
    this.isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
  }
  
  detectBestBaseUrl() {
    if (this.isLocalhost) {
      return 'http://localhost:5000';
    }
    return 'https://wyenfos-api-615596991593.asia-south1.run.app';
  }
  
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await corsSafeRequest(url, options);
      return response;
    } catch (error) {
      // If localhost fails and we're on localhost, try Firebase Functions
      if (this.isLocalhost && this.baseUrl.includes('localhost')) {
        console.log('🔄 Localhost failed, trying Firebase Functions...');
        const firebaseUrl = `https://wyenfos-api-615596991593.asia-south1.run.app${endpoint}`;
        return await corsSafeRequest(firebaseUrl, options);
      }
      throw error;
    }
  }
  
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }
  
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
  
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Global CORS error handler
export const handleCORSError = (error, retryCallback) => {
  const corsError = detectCORSError(error);
  
  if (corsError.isCORS) {
    console.error('🚫 CORS Error:', corsError.message);
    console.log('💡 Attempting to switch to localhost API...');
    
    // Try to switch to localhost API
    if (retryCallback) {
      return retryCallback();
    }
  }
  
  throw error;
};

// CORS status checker
export const checkCORSStatus = async () => {
  const endpoints = [
    '/health',
    '/api/products',
    '/api/customers',
    '/api/staff',
    '/api/companies'
  ];
  
  const results = {};
  
  for (const endpoint of endpoints) {
    try {
      // Test localhost first
      const localhostUrl = `http://localhost:5000${endpoint}`;
      const localhostResponse = await fetch(localhostUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      results[`localhost${endpoint}`] = {
        status: localhostResponse.ok ? 'success' : 'failed',
        statusCode: localhostResponse.status
      };
    } catch (error) {
      results[`localhost${endpoint}`] = {
        status: 'error',
        error: error.message
      };
    }
    
    try {
      // Test Firebase Functions
      const firebaseUrl = `https://wyenfos-api-615596991593.asia-south1.run.app${endpoint}`;
      const firebaseResponse = await fetch(firebaseUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      results[`firebase${endpoint}`] = {
        status: firebaseResponse.ok ? 'success' : 'failed',
        statusCode: firebaseResponse.status
      };
    } catch (error) {
      results[`firebase${endpoint}`] = {
        status: 'error',
        error: error.message
      };
    }
  }
  
  return results;
};

const corsFixUtils = {
  detectCORSError,
  getFallbackApiUrl,
  corsSafeRequest,
  CORSSmartClient,
  handleCORSError,
  checkCORSStatus
};

export default corsFixUtils;
